/**
 * Per-AP radio override view helpers -- PURE, so they are unit-tested (`npm run test:unit`) and
 * shared by components/device/DeviceRadiosSection.tsx and routes/RadioSettings.tsx. No React,
 * no fetch.
 *
 * Model (OP#1760): per band and field (channel / channel_width / tx_power) an AP either
 * INHERITS the site ("Site default (X)") or OVERRIDES it ("Overridden on this AP"). The form
 * keeps one choice per field: "inherit", or a concrete value. buildOverrideBody turns the form
 * into the minimal PUT body: only fields whose choice differs from today's override are sent,
 * `null` reverts a field to the site.
 */
import type {
  DeviceRadioBand,
  DeviceRadioField,
  RadioField,
  RadioOverrideBody,
  RadioOverrideResult,
  RadioValue,
} from "@/lib/api/deviceRadios";

/** StatusPill status values (components/ui/StatusPill). */
export type Tone = "healthy" | "warn" | "critical" | "info" | "unknown";

export const FIELD_LABEL: Record<RadioField, string> = {
  channel: "Channel",
  channel_width: "Width",
  tx_power: "TX power",
};

export const RADIO_FIELDS: RadioField[] = ["channel", "channel_width", "tx_power"];

/** Recommended 2.4 GHz width -- the controller default when nothing else sets one. */
export const RECOMMENDED_2G_WIDTH = 20;

/** Site Radio Settings width options per band. 2.4 GHz is 20/40 only (20 recommended). */
export const SITE_WIDTHS: Record<string, number[]> = {
  "2G": [20, 40],
  "5G": [20, 40, 80, 160],
  "6G": [20, 40, 80, 160, 320],
};

/** One value as an operator reads it. */
export function fmtRadioValue(field: RadioField, value: RadioValue, schemaDefaultWidth = 80): string {
  if (value === null || value === undefined) {
    return field === "channel_width" ? `not set (firmware ${schemaDefaultWidth} MHz)` : "not set";
  }
  if (value === "auto") return "Auto";
  if (field === "channel") return `Ch ${value}`;
  if (field === "channel_width") return `${value} MHz`;
  return `${value} dBm`;
}

/** "Overridden on this AP" vs "Site default (X)" vs "Default (X)". */
export function sourceText(field: RadioField, f: DeviceRadioField, schemaDefaultWidth = 80): string {
  if (f.source === "ap") return "Overridden on this AP";
  const v = fmtRadioValue(field, f.site_value, schemaDefaultWidth);
  return f.site_set ? `Site default (${v})` : `Default (${v})`;
}

/** The form's choice for one field: "inherit" or a concrete value. */
export type FieldChoice = "inherit" | RadioValue;
export type BandForm = Record<RadioField, FieldChoice>;

/** The form as the AP stands today: overridden fields carry their value, the rest inherit. */
export function initialForm(band: DeviceRadioBand): BandForm {
  const out = {} as BandForm;
  for (const field of RADIO_FIELDS) {
    const f = band.fields[field];
    out[field] = f.overridden ? f.override : "inherit";
  }
  return out;
}

/**
 * Minimal PUT body for the form: only fields whose choice differs from today's override.
 * "inherit" on an overridden field -> null (revert); a value -> that value. Empty -> {}.
 */
export function buildOverrideBody(band: DeviceRadioBand, form: BandForm): RadioOverrideBody {
  const body: RadioOverrideBody = {};
  for (const field of RADIO_FIELDS) {
    const f = band.fields[field];
    const choice = form[field];
    if (choice === "inherit") {
      if (f.overridden) body[field] = null;
    } else if (choice !== null && (!f.overridden || f.override !== choice)) {
      body[field] = choice;
    }
  }
  return body;
}

/** Fields a body touches (for "N changes" labels / disabling Preview). */
export function touchedFields(body: RadioOverrideBody): RadioField[] {
  return RADIO_FIELDS.filter((f) => f in body);
}

/** Width options for one band on this AP; empty when the AP lacks the band. */
export function widthOptions(band: DeviceRadioBand): number[] {
  return band.widths;
}

/** Fields the mesh pins on this AP (channel + width on the backhaul band). */
export function isPinned(band: DeviceRadioBand, field: RadioField): boolean {
  return band.mesh_pinned && (field === "channel" || field === "channel_width");
}

/** The push outcome as {tone, text} for a StatusPill + sentence. */
export function outcomeView(res: RadioOverrideResult): { tone: Tone; label: string; text: string } {
  switch (res.outcome) {
    case "applied":
      return { tone: "healthy", label: "Applied", text: "The AP took the new radio settings." };
    case "applied_with_warnings":
      return {
        tone: "warn",
        label: "Applied with warnings",
        text: res.warnings.length ? res.warnings.join("; ") : res.reason ?? "The AP dropped part of it.",
      };
    case "rejected":
      return {
        tone: "critical",
        label: "Rejected",
        text: `${res.reason ?? "Rejected by device."} The AP kept its previous settings; nothing was saved.`,
      };
    case "pending_confirmation":
      // owgw has it; the AP did not answer inside the controller's write timeout. Meridian
      // confirms it in the background and never re-sends it (OP#1775 follow-up).
      return {
        tone: "info",
        label: "Waiting for the AP to confirm",
        text: "owgw accepted it; the AP has not confirmed yet. Meridian keeps checking and records the result -- do not re-send.",
      };
    case "pending":
      return {
        tone: "info",
        label: "Sent",
        text: "owgw accepted it; the AP has not answered yet. Check back in a few minutes.",
      };
    case "unchanged":
      return {
        tone: "healthy",
        label: "Saved",
        text: "Saved on this AP. Its config already matched, so nothing was pushed.",
      };
    default:
      return { tone: "unknown", label: "Preview", text: "" };
  }
}

/** "3 APs have their own setting — kept" (site Radio Settings). Empty string for 0. */
export function keptOverridesText(n: number): string {
  if (!n) return "";
  return `${n} AP${n === 1 ? " has its" : "s have their"} own setting — kept`;
}

/** "channel-width" etc. snapshot key -> display line for the preview diff. */
export function snapshotLines(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): { key: string; before: string; after: string; changed: boolean }[] {
  const keys = ["channel", "channel-width", "tx-power"];
  const show = (k: string, v: unknown) => {
    if (v === undefined) {
      if (k === "tx-power") return "auto";
      if (k === "channel") return "auto";
      return "not set";
    }
    if (k === "channel-width") return `${v} MHz`;
    if (k === "tx-power") return `${v} dBm`;
    return String(v);
  };
  return keys.map((k) => ({
    key: k,
    before: show(k, before[k]),
    after: show(k, after[k]),
    changed: show(k, before[k]) !== show(k, after[k]),
  }));
}

/**
 * A note when what the AP runs NOW differs from what the controller intends, or "" for none.
 *
 * Only where the controller has a definite intent: an AP override, a site value, or the 2.4 GHz
 * width default (rendered on the AP's next push). A field that merely inherits an unset site
 * value is not re-rendered by a push, so a difference there is not a pending change.
 */
export function liveNote(band: DeviceRadioBand, field: RadioField): string {
  const f = band.fields[field];
  if (f.live === f.effective) return "";
  const is2gWidthDefault = band.band === "2G" && field === "channel_width" && f.source === "default";
  if (f.source === "default" && !is2gWidthDefault) return "";
  if (is2gWidthDefault && (f.live === null || f.live === undefined)) {
    return `AP has no width set (firmware falls back to 40 MHz); ${f.effective} MHz on its next push`;
  }
  return `AP currently runs ${fmtRadioValue(field, f.live, band.schema_default_width)}`;
}
